import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { clinicalNoteService } from '../services/clinicalNoteService';
import { patientService } from '../services/patientService';
import { aiService } from '../services/aiService';
import CommentThread from '../components/CommentThread';

const emptyForm = {
  patientId: '',
  noteType: 'SOAP',
  title: '',
  sessionNotes: '',
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
  data: '',
  intervention: '',
  response: '',
  behavior: '',
  content: '',
};

const ProgressNotes = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(() => {
    try {
      const prefs = JSON.parse(localStorage.getItem('peleescribe_prefs') || '{}');
      return { ...emptyForm, noteType: prefs.defaultNoteType || 'SOAP' };
    } catch {
      return emptyForm;
    }
  });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDisclaimer, setAiDisclaimer] = useState('');
  const [filterPatient, setFilterPatient] = useState('');

  const patientLabel = (p) =>
    p?.fullName || [p?.firstName, p?.lastName].filter(Boolean).join(' ') || 'Patient';

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [n, p] = await Promise.all([
        clinicalNoteService.listAll(),
        patientService.getPatients({}),
      ]);
      setNotes(n.data || []);
      setPatients(Array.isArray(p) ? p : p.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const pid = searchParams.get('patientId');
    if (pid) {
      setForm((f) => ({ ...f, patientId: pid }));
      setFilterPatient(pid);
      setShowForm(true);
    }
  }, [searchParams]);

  const buildPayload = () => {
    const noteType = form.noteType;
    let sections = {};
    let content = form.content;
    if (noteType === 'SOAP') {
      sections = {
        subjective: form.subjective,
        objective: form.objective,
        assessment: form.assessment,
        plan: form.plan,
      };
      content = `Subjective:\n${form.subjective}\n\nObjective:\n${form.objective}\n\nAssessment:\n${form.assessment}\n\nPlan:\n${form.plan}`;
    } else if (noteType === 'DAP') {
      sections = { data: form.data, assessment: form.assessment, plan: form.plan };
      content = `Data:\n${form.data}\n\nAssessment:\n${form.assessment}\n\nPlan:\n${form.plan}`;
    } else if (noteType === 'BIRP') {
      sections = {
        behavior: form.behavior,
        intervention: form.intervention,
        response: form.response,
        plan: form.plan,
      };
      content = `Behavior:\n${form.behavior}\n\nIntervention:\n${form.intervention}\n\nResponse:\n${form.response}\n\nPlan:\n${form.plan}`;
    }
    return {
      patientId: form.patientId,
      noteType,
      title: form.title || `${noteType} progress note`,
      content,
      sections,
      isAiDraft: !!aiDisclaimer,
    };
  };

  const runAiDraft = async () => {
    if (!form.sessionNotes.trim()) {
      alert('Paste session notes or a rough transcript first');
      return;
    }
    try {
      setAiBusy(true);
      setAiDisclaimer('');
      const res = await aiService.draftProgressNote({
        patientId: form.patientId || undefined,
        noteType: form.noteType,
        sessionNotes: form.sessionNotes,
      });
      const d = res.data || {};
      const s = d.sections || {};
      setForm((prev) => ({
        ...prev,
        subjective: s.subjective || prev.subjective,
        objective: s.objective || prev.objective,
        assessment: s.assessment || prev.assessment,
        plan: s.plan || prev.plan,
        data: s.data || prev.data,
        behavior: s.behavior || prev.behavior,
        intervention: s.intervention || prev.intervention,
        response: s.response || prev.response,
        content: d.content || s.content || prev.content,
      }));
      setAiDisclaimer(d.disclaimer || 'AI draft — review before signing');
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'AI draft failed');
    } finally {
      setAiBusy(false);
    }
  };

  const saveNote = async (e) => {
    e.preventDefault();
    if (!form.patientId) {
      alert('Select a client');
      return;
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      if (editingId) {
        await clinicalNoteService.update(editingId, payload);
      } else {
        await clinicalNoteService.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyForm, noteType: form.noteType });
      setAiDisclaimer('');
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (note) => {
    const s = note.sections || {};
    setEditingId(note._id);
    setForm({
      ...emptyForm,
      patientId: note.patientId?._id || note.patientId || '',
      noteType: note.noteType || 'SOAP',
      title: note.title || '',
      subjective: s.subjective || '',
      objective: s.objective || '',
      assessment: s.assessment || '',
      plan: s.plan || '',
      data: s.data || '',
      behavior: s.behavior || '',
      intervention: s.intervention || '',
      response: s.response || '',
      content: note.content || '',
      sessionNotes: '',
    });
    setAiDisclaimer(note.isAiDraft ? 'Previously AI-drafted — review carefully' : '');
    setShowForm(true);
  };

  const filtered = filterPatient
    ? notes.filter((n) => String(n.patientId?._id || n.patientId) === filterPatient)
    : notes;

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-semibold text-primary">Progress Notes</h1>
              <p className="text-gray-500 text-sm mt-1">
                SOAP / DAP / BIRP notes with optional Claude AI draft. Always clinician-reviewed before sign &amp; lock.
              </p>
            </div>
            <button
              type="button"
              className="bg-primary text-white px-4 py-2 rounded-lg"
              onClick={() => {
                setEditingId(null);
                setAiDisclaimer('');
                setShowForm(!showForm);
              }}
            >
              {showForm ? 'Cancel' : 'New progress note'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={saveNote} className="bg-white rounded-xl shadow p-6 mb-6 space-y-4">
              <div className="grid md:grid-cols-3 gap-3">
                <label className="text-sm flex flex-col gap-1">
                  Client *
                  <select
                    className="border rounded px-3 py-2"
                    value={form.patientId}
                    onChange={(e) => setForm({ ...form, patientId: e.target.value })}
                    required
                    disabled={!!editingId}
                  >
                    <option value="">Select…</option>
                    {patients.map((p) => (
                      <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm flex flex-col gap-1">
                  Note type
                  <select
                    className="border rounded px-3 py-2"
                    value={form.noteType}
                    onChange={(e) => setForm({ ...form, noteType: e.target.value })}
                  >
                    <option value="SOAP">SOAP</option>
                    <option value="DAP">DAP</option>
                    <option value="BIRP">BIRP</option>
                    <option value="progress">Progress narrative</option>
                  </select>
                </label>
                <label className="text-sm flex flex-col gap-1">
                  Title
                  <input
                    className="border rounded px-3 py-2"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </label>
              </div>

              <div className="border rounded-lg p-4 bg-blue-50/50 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-medium text-sm">Claude AI draft assist</h3>
                  <button
                    type="button"
                    disabled={aiBusy}
                    onClick={runAiDraft}
                    className="bg-primary text-white text-sm px-3 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    {aiBusy ? 'Drafting…' : 'Generate AI draft'}
                  </button>
                </div>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={4}
                  placeholder="Paste rough session notes or scribe transcript here, then generate a structured draft…"
                  value={form.sessionNotes}
                  onChange={(e) => setForm({ ...form, sessionNotes: e.target.value })}
                />
                {aiDisclaimer && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    {aiDisclaimer}
                  </p>
                )}
              </div>

              {form.noteType === 'SOAP' && (
                <div className="grid md:grid-cols-2 gap-3">
                  {['subjective', 'objective', 'assessment', 'plan'].map((k) => (
                    <label key={k} className="text-sm flex flex-col gap-1 capitalize">
                      {k}
                      <textarea
                        className="border rounded px-3 py-2"
                        rows={3}
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
              )}
              {form.noteType === 'DAP' && (
                <div className="grid md:grid-cols-1 gap-3">
                  {['data', 'assessment', 'plan'].map((k) => (
                    <label key={k} className="text-sm flex flex-col gap-1 capitalize">
                      {k}
                      <textarea
                        className="border rounded px-3 py-2"
                        rows={3}
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
              )}
              {form.noteType === 'BIRP' && (
                <div className="grid md:grid-cols-2 gap-3">
                  {['behavior', 'intervention', 'response', 'plan'].map((k) => (
                    <label key={k} className="text-sm flex flex-col gap-1 capitalize">
                      {k}
                      <textarea
                        className="border rounded px-3 py-2"
                        rows={3}
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
              )}
              {form.noteType === 'progress' && (
                <label className="text-sm flex flex-col gap-1">
                  Narrative
                  <textarea
                    className="border rounded px-3 py-2"
                    rows={8}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                  />
                </label>
              )}

              <button
                type="submit"
                disabled={saving}
                className="bg-primary text-white px-5 py-2 rounded-lg"
              >
                {saving ? 'Saving…' : editingId ? 'Update draft' : 'Save as draft'}
              </button>
            </form>
          )}

          <div className="mb-4">
            <select
              className="border rounded px-3 py-2 text-sm"
              value={filterPatient}
              onChange={(e) => setFilterPatient(e.target.value)}
            >
              <option value="">All clients</option>
              {patients.map((p) => (
                <option key={p._id} value={p._id}>{patientLabel(p)}</option>
              ))}
            </select>
          </div>

          {loading && <p>Loading…</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && (
            <div className="space-y-4">
              {filtered.length === 0 && (
                <p className="text-gray-500">No progress notes yet.</p>
              )}
              {filtered.map((note) => (
                <div key={note._id} className="bg-white rounded-xl shadow p-4">
                  <div className="flex flex-wrap justify-between gap-2 mb-2">
                    <div>
                      <div className="font-medium">{note.title || 'Untitled'}</div>
                      <div className="text-xs text-gray-500">
                        {patientLabel(note.patientId)} · {note.noteType} ·{' '}
                        {note.createdAt ? new Date(note.createdAt).toLocaleString() : ''}
                        {note.isAiDraft ? ' · AI draft' : ''}
                      </div>
                    </div>
                    <span className="text-xs capitalize bg-gray-100 px-2 py-1 rounded h-fit">
                      {note.clinicalStatus}
                    </span>
                  </div>
                  <pre className="text-sm whitespace-pre-wrap text-gray-700 max-h-40 overflow-y-auto bg-gray-50 rounded p-3">
                    {(note.content || '').slice(0, 1200)}
                    {(note.content || '').length > 1200 ? '…' : ''}
                  </pre>
                  <div className="flex flex-wrap gap-3 mt-3 text-sm">
                    {note.clinicalStatus === 'draft' && (
                      <>
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={() => openEdit(note)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={async () => {
                            if (!window.confirm('Sign and lock this note? It cannot be edited afterward.')) {
                              return;
                            }
                            const createBilling = window.confirm(
                              'Also create invoice + claim draft from AI billing hint?',
                            );
                            try {
                              const res = await clinicalNoteService.sign(note._id, {
                                createBilling: !!createBilling,
                              });
                              const billing = res.data?.billing || res.billing;
                              if (billing?.created) {
                                alert(
                                  `Note signed. Invoice ${billing.created.invoice?.invoiceNumber || ''} and claim draft created.`,
                                );
                              } else if (billing?.suggestion) {
                                const s = billing.suggestion;
                                const go = window.confirm(
                                  `Note signed.\nSuggested CPT: ${s.suggestedCptCode || '—'}\n${s.rationale || ''}\n\nCreate invoice + claim now?`,
                                );
                                if (go) {
                                  await clinicalNoteService.createBilling(note._id);
                                  alert('Invoice and claim draft created.');
                                }
                              } else {
                                alert('Note signed and locked.');
                              }
                              await load();
                            } catch (e) {
                              alert(e.response?.data?.message || e.message);
                            }
                          }}
                        >
                          Sign &amp; lock
                        </button>
                        <button
                          type="button"
                          className="text-gray-600 underline"
                          onClick={async () => {
                            try {
                              const hint = await aiService.billingHint({
                                noteText: note.content || '',
                              });
                              const d = hint.data || {};
                              alert(
                                `Suggested CPT: ${d.suggestedCptCode || '—'}\nUnits: ${d.units}\n${d.rationale || ''}\n\n${d.disclaimer || ''}`,
                              );
                            } catch (e) {
                              alert(e.response?.data?.message || e.message);
                            }
                          }}
                        >
                          AI billing hint
                        </button>
                      </>
                    )}
                    {note.clinicalStatus === 'locked' && (
                      <>
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={async () => {
                            try {
                              await clinicalNoteService.createBilling(note._id);
                              alert('Invoice + claim draft created from this note.');
                            } catch (e) {
                              alert(e.response?.data?.message || e.message);
                            }
                          }}
                        >
                          Create invoice
                        </button>
                        <button
                          type="button"
                          className="text-gray-600 underline"
                          onClick={() => {
                            const w = window.open('', '_blank');
                            if (!w) return;
                            w.document.write(`<!doctype html><html><head><title>${note.title || 'Note'}</title>
                              <style>body{font-family:Georgia,serif;padding:32px;max-width:720px;margin:auto}
                              h1{font-size:18px} .meta{color:#666;font-size:12px;margin-bottom:16px}
                              pre{white-space:pre-wrap;font-family:inherit;line-height:1.5}</style></head><body>
                              <h1>${note.title || 'Progress note'}</h1>
                              <div class="meta">${note.noteType || ''} · Signed ${note.signedAt ? new Date(note.signedAt).toLocaleString() : ''} · ${patientLabel(note.patientId)}</div>
                              <pre>${(note.content || '').replace(/</g, '&lt;')}</pre>
                              <script>window.print()</script></body></html>`);
                            w.document.close();
                          }}
                        >
                          Print / PDF
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="text-gray-600 underline"
                      onClick={() =>
                        navigate(`/patient/${note.patientId?._id || note.patientId}`)
                      }
                    >
                      Open chart
                    </button>
                  </div>
                  <CommentThread
                    targetType="clinical_note"
                    targetId={note._id}
                    patientId={note.patientId?._id || note.patientId}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProgressNotes;
